import { Nav, NavItem, NavLink } from '../Bootstrap';
import React from 'react';
import { useParams, useLocation, useHistory } from 'react-router';
import classnames from 'classnames';
import { AuthContext } from '../../AuthService';
import { NavbarContext } from '../../context';
import { useQueryParams } from '../../hooks';

import { Demographics } from './Demographics';
import { Home } from './Home';
import { Menu } from './Menu';
import { MyVenue } from './MyVenue';
import { Order } from './Order';
import { Reports } from './Reports';
import { FulfillmentStation } from './FulfillmentStation';
import { Users } from './Users';
import './Navigation.scss';

import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { Logout } from './Logout';
const logoUrl = require('./noble.png');

interface VenuePortalRouterParams {
  venueId: string;
}

const NobleNavLink: React.FC<{ link: any }> = ({ link }) => {
  return (
    <NavItem key={link.link}>
      <OverlayTrigger
        delay={{ hide: 450, show: 300 }}
        overlay={(props) => <Tooltip {...props}>{link.title}</Tooltip>}
        placement='bottom'
      >
        <NavLink href={link.link} active={link.active}>
          <div className='nav-icon'>{link.icon ? link.icon : link.title}</div>
          <div className='nav-link-text'>{link.title}</div>
        </NavLink>
      </OverlayTrigger>
    </NavItem>
  );
};

export const Navigation = () => {
  const { venueId } = useParams<VenuePortalRouterParams>();
  const queryParams = useQueryParams();
  const location = useLocation();
  const history = useHistory();

  const start = queryParams.get('start');
  const end = queryParams.get('end');
  const suffix = start && end ? `?start=${start}&end=${end}` : '';

  const { isOpen, toggle } = React.useContext(NavbarContext);

  const auth = React.useContext(AuthContext);
  const onClickLogout = React.useCallback(() => {
    auth.logout();
    history.push('/');
  }, [auth, history]);

  const permissions = auth.getPermissions();

  const path = location.pathname;

  const headerLinks = [
    {
      link: `/venue/${venueId}/dashboard${suffix}`,
      icon: <Home />,
      active: path.includes('dashboard'),
      title: 'Dashboard',
    },
  ];

  if (permissions.includes('read:venue-demographics')) {
    headerLinks.push({
      link: `/venue/${venueId}/demographics${suffix}`,
      icon: <Demographics />,
      active: path.includes('demographics'),
      title: 'Demographics',
    });
  }

  if (permissions.includes('read:menu-items')) {
    headerLinks.push({
      link: `/venue/${venueId}/menu/list`,
      icon: <Menu />,
      active: path.includes('menu'),
      title: 'Menu Builder',
    });
  }

  if (permissions.includes('read:fulfillment-centers')) {
    headerLinks.push({
      link: `/venue/${venueId}/fulfillment-centers`,
      icon: <FulfillmentStation />,
      active: path.includes('fulfillment'),
      title: 'Fulfillment Centers',
    });
  }

  if (permissions.includes('read:session-reports')) {
    headerLinks.push({
      link: `/venue/${venueId}/reports/sessions${suffix}`,
      icon: <Reports />,
      active: path.includes('reports'),
      title: 'Reports',
    });
  }

  if (permissions.includes('read:operator-users')) {
    headerLinks.push({
      link: `/venue/${venueId}/users`,
      icon: <Users />,
      active: path.includes('users'),
      title: 'Users',
    });
  }

  if (permissions.includes('update:venue')) {
    headerLinks.push({
      link: `/venue/${venueId}/manage`,
      icon: <MyVenue />,
      active: path.includes('manage'),
      title: 'Manage',
    });
  }

  if (permissions.includes('refund:orders')) {
    headerLinks.push({
      link: `/venue/${venueId}/order`,
      icon: <Order />,
      active: path.includes('order'),
      title: 'Orders',
    });
  }

  return (
    <div className={classnames('noble-navbar-wrapper', isOpen && 'noble-navbar-wrapper-open')} onClick={toggle}>
      <header className='noble-navbar'>
        <a className='noble-navbar-brand' href={`/venue/${venueId}/dashboard`}>
          <img src={logoUrl} alt='Noble' />
        </a>
        <Nav className='ms-auto'>
          {headerLinks.map((link) => (
            <NobleNavLink key={link.link} link={link} />
          ))}
          <NavItem className='noble-navbar-logout'>
            <NavLink onClick={onClickLogout}>
              <Logout />
            </NavLink>
          </NavItem>
        </Nav>
      </header>
    </div>
  );
};
